import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import EventCard from "./EventCard";
import DispatchModal from "./DispatchModal";
import CloseAlarmModal from "./CloseAlarmModal";
import ZoneMapSVG from "./ZoneMapSVG";
import SigRef from "./SigRef";
import BatteryStatus from "./BatteryStatus";
import Clock from "./Clock";
import SupervisorView from "./SupervisorView";
import { ALARM_LIMIT_IN_SEC } from "./helpers";
import { UserAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { isToday } from "./helpers";
import { schema } from "../schema";
import packageJson from "../../package.json";

const DARK = {
  minHeight: "100vh",
  background: "var(--bg)",
  color: "var(--text)",
  fontFamily: "Inter, -apple-system, sans-serif",
};

/** Counter chip */
function Ctr({ value, label, color, hot }) {
  return (
    <div style={{
      flex: 1, padding: "10px 8px",
      background: hot ? `${color}10` : "var(--card)",
      border: `1px solid ${hot ? `${color}40` : "var(--border)"}`,
      borderRadius: 8, minWidth: 0,
    }}>
      <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: "var(--mono)", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
    </div>
  );
}

function differenceInSeconds(date) {
  return moment(new Date()).diff(moment(date), "seconds");
}

function getZoneOpacity(zoneID, alarms) {
  const now = Date.now();
  const active = alarms.filter(a => String(a[schema.zoneField]) === String(zoneID) && !a.resolvedAt);
  if (!active.length) return "opacity-0";
  const recent = active.some(a => a.updatedAt && (now / 1000 - a.updatedAt.seconds) < ALARM_LIMIT_IN_SEC);
  return recent ? "opacity-100" : "opacity-0";
}

/**
 * MapWithList — main operator view containing:
 * – Header with counters, map, SigRef, open events, closed events (last 24h)
 * – Tab toggle: Operador / Supervisor
 *
 * Props: title, alarms, facility, zones, operators, knownGuards
 */
export default function MapWithList({ title, alarms = [], facility, zones, operators = [], knownGuards = [] }) {
  const { logout, user } = UserAuth();

  const isSupervisor = user?.uid && facility?.supervisorUID && user.uid === facility.supervisorUID;
  // Jefe de seguridad (chiefUID, Fase 2): entra a la vista Supervisor para revisar/validar.
  const isChief = user?.uid && facility?.chiefUID && user.uid === facility.chiefUID;
  // Usuario dev: misma vista y permisos que un operador, pero con el toggle de mapa experimental.
  const isDev = user?.uid && facility?.devUID && user.uid === facility.devUID;
  const expMap = facility?.experimentalMap;
  const canUseExpMap = Boolean(isDev && expMap?.enabled && expMap?.url);
  const [showExpMap, setShowExpMap] = useState(false); // false = mapa actual, true = iframe nuevo

  const [view, setView]               = useState(isSupervisor || isChief ? "sup" : "op");
  const [modal, setModal]             = useState(null); // { type: "dispatch"|"close", alarm }

  // Si el usuario se carga un poco después o cambia isSupervisor/isChief, asegurar que vaya a "sup"
  useEffect(() => {
    if (isSupervisor || isChief) setView("sup");
  }, [isSupervisor, isChief]);
  
  const orderedOperators = useMemo(() => {
    const counts = {};
    (alarms || []).forEach(a => {
      const d = a.dispatchedBy;
      const c = a.closedBy;
      if (d) counts[d] = (counts[d] || 0) + 1;
      if (c && c !== d) counts[c] = (counts[c] || 0) + 1;
    });
    const merged = new Set([...(operators || []), ...Object.keys(counts)]);
    return Array.from(merged).sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
  }, [alarms, operators]);

  const [sortedAlarms, setSortedAlarms] = useState(alarms || []);

  useEffect(() => { setSortedAlarms(alarms || []); }, [alarms]);

  /* ── Derived lists ── */
  const open = sortedAlarms
    .filter(a => !a.resolvedAt)
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  const closedRecent = sortedAlarms
    .filter(a => a.resolvedAt && a.createdAt?.seconds && isToday(a.createdAt.seconds * 1000))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  /* ── 30s tick to refresh time-based KPIs ── */
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(k => k + 1), 30000); return () => clearInterval(t); }, []);

  const active    = sortedAlarms.filter(a => !a.resolvedAt && (a.date ? (Date.now() - a.date.getTime()) / 1000 < ALARM_LIMIT_IN_SEC : false)).length;  // eslint-disable-line
  const unm       = sortedAlarms.filter(a => !a.resolvedAt && !a.dispatchedBy).length;
  const pend      = sortedAlarms.filter(a => !a.resolvedAt && a.dispatchedBy).length;
  const todayTot  = sortedAlarms.filter(a => a.createdAt?.seconds && isToday(a.createdAt.seconds * 1000)).length;

  /* ── Handlers ── */
  function handleCardClick(alarm) {
    if (alarm.resolvedAt) {
      setModal({ type: "close", alarm });
      return;
    }
    if (!alarm.dispatchedBy) {
      setModal({ type: "dispatch", alarm });
    } else {
      setModal({ type: "close", alarm });
    }
  }

  async function handleDispatch(docID, opName) {
    const ref = doc(db, schema.groupedAlarms, docID);
    await updateDoc(ref, {
      dispatchedBy: opName,
      dispatchTime: serverTimestamp(), // saved as server-side Timestamp
    });
    setModal(null);
  }

  async function handleArrive(docID) {
    const ref = doc(db, schema.groupedAlarms, docID);
    await updateDoc(ref, {
      arrivedTime: serverTimestamp(), // saved as server-side Timestamp
    });
  }

  async function handleReopen(alarm) {
    const ref = doc(db, schema.groupedAlarms, alarm.docID);
    await updateDoc(ref, {
      resolvedAt: null,
      reopenedAt: serverTimestamp(), // saved as server-side Timestamp
    });
  }

  return (
    <div style={DARK}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600;700&display=swap');
        button { font-family: inherit; }
      `}</style>

      {/* Sticky header */}
      <div style={{
        background: "var(--card)", borderBottom: "1px solid var(--border)",
        padding: "8px 12px", display: "flex", alignItems: "center",
        justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Logo placeholder */}
          <div style={{ width: 26, height: 26, background: "#dc2626", borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#fff" }}>T</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--bright)" }}>{title}</div>
            <div style={{ fontSize: 9, color: "var(--muted)" }}>{schema.platformName} v{packageJson.version} · {zones?.length || 0} zonas</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="hide-mobile"><Clock /></span>
          <span className="hide-mobile"><BatteryStatus facility={facility} /></span>
          {/* View toggle */}
          <div style={{ display: "flex", gap: 2, background: "var(--bg)", borderRadius: 6, padding: 2 }}>
            {[["op", "Operador"], ["sup", "Supervisor"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setView(k)} style={{
                padding: "4px 10px", borderRadius: 4, border: "none",
                background: view === k ? "var(--surface)" : "transparent",
                color: view === k ? "var(--bright)" : "var(--muted)",
                fontSize: 10, fontWeight: 600, cursor: "pointer",
              }}>{l}</button>
            ))}
          </div>
          {/* Logout */}
          <button 
            type="button" 
            onClick={() => setModal({ type: "logout" })} 
            title="Salir" 
            aria-label="Cerrar sesión"
            style={{ 
              background: "none", border: "none", cursor: "pointer", 
              color: "var(--muted)", display: "flex", alignItems: "center", justifyContent: "center",
              padding: "4px"
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
              <line x1="12" y1="2" x2="12" y2="12"></line>
            </svg>
          </button>

        </div>
      </div>

      {/* ── Operator view ── */}
      {view === "op" && (
        <div style={{ position: "relative", minHeight: "calc(100vh - 60px)" }}>
          {isSupervisor && (
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(15, 23, 42, 0.5)",
              zIndex: 50,
              cursor: "not-allowed"
            }}>
              <div style={{
                position: "sticky", top: 120,
                margin: "0 auto", width: "fit-content",
                background: "rgba(30, 41, 59, 0.95)", border: "1px solid #3b82f6", 
                color: "#bfdbfe", padding: "14px 24px", borderRadius: 8, 
                backdropFilter: "blur(12px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                textAlign: "center"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 800, letterSpacing: "0.03em" }}>
                  <span style={{ fontSize: 18 }}>👁️</span> VISTA EN VIVO - MODO LECTURA
                </div>
                <div style={{ fontSize: 11, color: "#60a5fa", fontWeight: 600 }}>
                  Estás viendo la misma información que el operador en tiempo real.<br/>No puedes modificar ni gestionar eventos desde este rol.
                </div>
              </div>
            </div>
          )}
          <div className="operator-container">
            <div className="operator-layout-grid">

              {/* Counters */}
              <div className="kpi-grid-op grid-area-counters" style={{ marginBottom: 10 }}>
                <Ctr value={active}   label="Activas"       color="#dc2626" hot={active   > 0} />
                <Ctr value={unm}      label="Sin despacho"  color="#f59e0b" hot={unm      > 0} />
                <Ctr value={pend}     label="Pendientes"    color="#0d9488" hot={pend     > 0} />
                <Ctr value={todayTot} label="Hoy"           color="var(--muted)" hot={false} />
              </div>

              {/* Map Area with absolute HUD Turn Overlay */}
              <div className="grid-area-map" style={{ position: "relative", marginBottom: 10 }}>
                {facility?.visibleHoursRange && (
                  <div style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 10,
                    background: "rgba(15, 23, 42, 0.75)",
                    backdropFilter: "blur(4px)",
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    borderRadius: 6,
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4.5,
                    pointerEvents: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
                  }}>
                    <span style={{
                      display: "inline-block",
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: "#22c55e",
                      boxShadow: "0 0 6px #22c55e",
                      animation: "pulse 2s infinite"
                    }}></span>
                    <span style={{
                      color: "#22c55e",
                      fontWeight: 700,
                      fontSize: 9.5,
                      fontFamily: "var(--mono)",
                      letterSpacing: "0.03em"
                    }}>
                      TURNO: {String(facility.visibleHoursRange[0]).padStart(2, "0")}:00 - {String(facility.visibleHoursRange[1]).padStart(2, "0")}:00
                    </span>
                  </div>
                )}

                {canUseExpMap && showExpMap ? (
                  /* Mapa experimental (iframe) — solo dev, reemplaza únicamente el mapa */
                  <iframe
                    title="Mapa experimental"
                    src={expMap.url}
                    style={{ width: "100%", height: 340, border: "none", borderRadius: 8, background: "#1e293b", display: "block" }}
                  />
                ) : (
                <>
                {/* Image map (legacy) */}
                {!facility.noMap && (
                  <div className="relative" style={{ marginBottom: 0 }}>
                    {facility.image && (
                      <img 
                        alt={title} 
                        src={(() => { try { return require(`../assets/${facility.image}`); } catch(e) { return ""; } })()} 
                        className="h-full w-full" 
                        style={{ borderRadius: 8, minHeight: 100, background: "#1e293b" }} 
                      />
                    )}
                    {zones?.map(zone => (
                      <div className="absolute inset-0" key={zone.id}>
                        {zone.img && (
                          <img
                            alt={zone.zoneName}
                            src={(() => { try { return require(`../assets/${zone.img}.png`); } catch(e) { return ""; } })()}
                            className={`h-full w-full transition-opacity ${getZoneOpacity(zone.id, sortedAlarms)}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* SVG map (optional, shown if zones have svgPoints) */}
                <ZoneMapSVG zones={zones} alarms={sortedAlarms} onZoneClick={handleCardClick} />
                </>
                )}

                {/* Toggle mapa nuevo (beta) — discreto, abajo-izquierda, solo dev */}
                {canUseExpMap && (
                  <button
                    type="button"
                    onClick={() => setShowExpMap(v => !v)}
                    title={showExpMap ? "Volver al mapa actual" : "Ver el mapa nuevo (beta)"}
                    style={{
                      position: "absolute", bottom: 8, left: 8, zIndex: 20,
                      display: "flex", alignItems: "center", gap: 5,
                      background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(4px)",
                      border: "1px solid rgba(148, 163, 184, 0.35)", borderRadius: 6,
                      padding: "4px 9px", cursor: "pointer",
                      color: showExpMap ? "#22c55e" : "#94a3b8", fontWeight: 700, fontSize: 9.5,
                      fontFamily: "var(--mono)", letterSpacing: "0.03em",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
                    }}
                  >
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: showExpMap ? "#22c55e" : "#94a3b8", boxShadow: showExpMap ? "0 0 6px #22c55e" : "none" }} />
                    {showExpMap ? "MAPA NUEVO (BETA)" : "MAPA ACTUAL"}
                  </button>
                )}
              </div>

              {/* Signal reference */}
              <div className="grid-area-sigref">
                <SigRef />
              </div>

              {/* Open events */}
              <div className="grid-area-openevents">
                {open.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#dc2626", animation: "pulse 1.5s infinite" }} />
                      Eventos abiertos ({open.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {open.map(a => (
                        <EventCard
                          key={a.docID}
                          alarm={a}
                          onClick={handleCardClick}
                          onArrive={handleArrive}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {open.length === 0 && (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)" }}>
                    <div style={{ fontSize: 48, marginBottom: 8 }}>👮</div>
                    <div style={{ fontWeight: 600 }}>Sin eventos activos en este momento.</div>
                  </div>
                )}
              </div>

              {/* Closed last 24h */}
              <div className="grid-area-closedevents">
                {closedRecent.length > 0 && (
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
                      Cerrados · últimas 24h ({closedRecent.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {closedRecent.map(a => (
                        <EventCard key={a.docID} alarm={a} compact onClick={handleCardClick} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── Supervisor view ── */}
      {view === "sup" && (
        <SupervisorView facility={facility} zones={zones} isSupervisor={isSupervisor} />
      )}

      {/* ── Modals ── */}
      {modal?.type === "dispatch" && (() => {
        const liveAlarm = (alarms || []).find(a => a.docID === modal.alarm.docID) || modal.alarm;
        return (
          <DispatchModal
            alarm={liveAlarm}
            operators={orderedOperators}
            nameAliases={facility.nameAliases}
            onDispatch={handleDispatch}
            onCancel={() => setModal(null)}
          />
        );
      })()}
      {modal?.type === "close" && (() => {
        const liveAlarm = (alarms || []).find(a => a.docID === modal.alarm.docID) || modal.alarm;
        return (
          <CloseAlarmModal
            alarmObj={liveAlarm}
            facilityId={facility.id}
            operators={orderedOperators}
            knownGuards={knownGuards}
            nameAliases={facility.nameAliases}
            showSiren={facility.showSiren ?? false}
            isReopen={!!liveAlarm.resolvedAt}
            onClose={() => setModal(null)}
          />
        );
      })()}
      {modal?.type === "logout" && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: "var(--card)", padding: 24, borderRadius: 12,
            border: "1px solid var(--border)", width: "90%", maxWidth: 320,
            textAlign: "center", boxShadow: "0 10px 25px rgba(0,0,0,0.5)"
          }}>
            <h3 style={{ margin: "0 0 10px 0", color: "var(--text)", fontSize: 18 }}>¿Cerrar sesión?</h3>
            <p style={{ margin: "0 0 24px 0", color: "var(--muted)", fontSize: 14, lineHeight: 1.4 }}>
              ¿Estás seguro de que deseas salir de la plataforma? Dejarás de recibir alertas en esta sesión.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                type="button"
                onClick={() => setModal(null)}
                style={{
                  flex: 1, padding: "10px", borderRadius: 8, border: "1px solid var(--border)",
                  background: "var(--surface)", color: "var(--text)", fontWeight: 600, cursor: "pointer"
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={logout}
                style={{
                  flex: 1, padding: "10px", borderRadius: 8, border: "none",
                  background: "#dc2626", color: "#fff", fontWeight: 600, cursor: "pointer"
                }}
              >
                Salir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
